/**
 * react-big-calendar Wrapper mit deutscher Lokalisierung + Tanzraum CI.
 * - Wochenansicht als Default, da Charlie/Mieter v.a. wöchentlich planen
 * - 1 Resource (= 1 Raum); die Ansicht über alle Räume liegt in AllRoomsCalendar
 * - Blockierte/gebuchte Slots werden read-only farblich abgesetzt
 */

import { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  type Event as RbcEvent,
  type SlotInfo,
  type View,
} from "react-big-calendar";
import { startOfWeek } from "date-fns";
import { de } from "date-fns/locale";

import { useCalendarFeed } from "@/hooks/useCalendar";
import {
  calendarFormats as buildFormats,
  calendarMessages,
  eventEnd,
  feedEventDetail,
  localizer,
} from "@/lib/calendar-locale";
import { type EventDetail, EventDetailDialog } from "@/components/EventDetailDialog";
import type { BookingCalendarEvent, Room } from "@/types/api";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./booking-calendar.css";


const messages = calendarMessages();

interface BookingCalendarProps {
  room: Room;
  onSelectSlot?: (slot: { start: Date; end: Date }) => void;
  selected?: { start: Date; end: Date } | null;
  date?: Date;
  onNavigate?: (date: Date) => void;
}

type SlotKind = BookingCalendarEvent["kind"] | "selected";

interface AugmentedEvent extends RbcEvent {
  kind: SlotKind;
  detail?: EventDetail;
}

function rangeForView(view: View, current: Date): { starts_at: string; ends_at: string } {
  const start = new Date(current);
  const end = new Date(current);
  if (view === "month") {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 1);
  } else if (view === "day") {
    end.setDate(end.getDate() + 1);
  } else {
    const monday = startOfWeek(start, { locale: de });
    start.setTime(monday.getTime());
    end.setTime(monday.getTime());
    end.setDate(end.getDate() + 7);
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return { starts_at: start.toISOString(), ends_at: end.toISOString() };
}

function isMobileViewport(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(max-width: 640px)").matches;
}

const calendarFormats = buildFormats();

export function BookingCalendar({
  room,
  onSelectSlot,
  selected,
  date,
  onNavigate,
}: BookingCalendarProps) {
  const [isMobile, setIsMobile] = useState<boolean>(() => isMobileViewport());
  const [view, setView] = useState<View>(() => (isMobileViewport() ? "day" : "week"));
  const [internalCurrent, setInternalCurrent] = useState<Date>(() => new Date());
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const current = date ?? internalCurrent;
  const setCurrent = (next: Date) => {
    if (onNavigate) onNavigate(next);
    else setInternalCurrent(next);
  };
  const range = useMemo(() => rangeForView(view, current), [view, current]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      setView((prev) => {
        if (e.matches && prev === "week") return "day";
        if (!e.matches && prev === "day") return "week";
        return prev;
      });
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const { data, isLoading } = useCalendarFeed({
    starts_at: range.starts_at,
    ends_at: range.ends_at,
    room_id: room.id,
  });

  const events: AugmentedEvent[] = useMemo(() => {
    const base: AugmentedEvent[] = (data ?? []).map((e) => {
      const name = e.title ?? (e.kind === "booked" ? "Belegt" : "Geschlossen");
      return {
        title: name,
        detail: feedEventDetail(e, name, room.name),
        start: new Date(e.starts_at),
        end: eventEnd(e.ends_at),
        kind: e.kind,
      };
    });
    if (selected) {
      base.push({
        title: "Dein Slot",
        start: selected.start,
        end: selected.end,
        kind: "selected",
      });
    }
    return base;
  }, [data, selected, room.name]);

  function handleSelectSlot(slot: SlotInfo) {
    if (!onSelectSlot) return;
    const slotStart = slot.start instanceof Date ? slot.start : new Date(slot.start);
    let slotEnd = slot.end instanceof Date ? slot.end : new Date(slot.end);
    const minDuration = Math.max(60, room.min_duration_minutes);
    const actualMinutes = (slotEnd.getTime() - slotStart.getTime()) / 60000;
    if (actualMinutes < minDuration) {
      slotEnd = new Date(slotStart.getTime() + minDuration * 60000);
    }
    const conflict = events.some((e) => {
      if (e.kind === "selected") return false;
      if (!e.start || !e.end) return false;
      const eStart = e.start instanceof Date ? e.start : new Date(e.start);
      const eEnd = e.end instanceof Date ? e.end : new Date(e.end);
      return eStart < slotEnd && eEnd > slotStart;
    });
    if (conflict) return;
    onSelectSlot({ start: slotStart, end: slotEnd });
  }

  return (
    <div className="rounded-card bg-surface p-4 shadow-card md:p-6">
      {isLoading && (
        <div className="mb-3 text-body-sm text-ink-muted">Kalender wird geladen…</div>
      )}
      <Calendar
        localizer={localizer}
        culture="de"
        messages={messages}
        formats={calendarFormats}
        events={events}
        view={view}
        onView={setView}
        date={current}
        onNavigate={setCurrent}
        views={isMobile ? ["day", "week"] : ["week", "day", "month"]}
        defaultView={isMobile ? "day" : "week"}
        // Events über Mitternacht (Partys bis 02:00) ins Raster statt in die Ganztags-Zeile.
        showMultiDayTimes
        step={30}
        timeslots={2}
        min={new Date(1970, 0, 1, 7, 0)}
        max={new Date(1970, 0, 1, 23, 0)}
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={(event) => setDetail((event as AugmentedEvent).detail ?? null)}
        eventPropGetter={(event) => {
          const kind = (event as AugmentedEvent).kind;
          return {
            className: `rbc-event--${kind}`,
          };
        }}
        style={{ height: isMobile ? 520 : 640 }}
      />
      <EventDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
